import { query } from '@angular/animations';
import { CommonModule, DatePipe, JsonPipe, ɵnormalizeQueryParams } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule, MatDateRangeInput } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { IgxExcelExporterService, IgxGridModule } from '@infragistics/igniteui-angular';
import { IgxGridComponent, IgxTabsModule, IgxExcelExporterOptions } from '@infragistics/igniteui-angular';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { AuthService } from 'src/app/core/services/auth-security/auth.service';
import { MessageService } from 'src/app/core/services/broadcast/message-service';
import { DataService } from 'src/app/core/services/data/data.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import {
  IApnMassActivationSpaceEmailLink,
  IApnSpaceEmailLink,
  ICompaniesShortList,
  ILotList,
  IParkerActivationReturnData,
  IParkersReadyForactivation,
  IParkingAgreementReportList,
  IParkingAgreementReportQueryParams,
  ITenantReport,
  ITenantReportQueryCriteria,
  ITenantReportCompanyTotals,
  ITenantExportReport,
} from 'src/app/shared/interfaces';
import { HeaderTitleLotDataComponent } from 'src/app/shared/ui/header-title-lot-data/header-title-lot-data.component';
import { YearSelectorDirectiveStandalone } from 'src/app/shared/directives/set-date-current-year-standalone.directive';

@Component({
  selector: 'app-tenant-report',
  standalone: true,
  imports: [
    MatSlideToggleModule,
    MatExpansionModule,
    MatIconModule,
    CommonModule,
    MatTabsModule,
    MatSnackBarModule,
    MatTableModule,
    MatCardModule,
    MatOptionModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    IgxGridModule,
    IgxTabsModule,
    IgxGridComponent,
    HeaderTitleLotDataComponent,
    MatSnackBarModule,
    MatProgressBarModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatIconModule,
    MatButtonModule,
    YearSelectorDirectiveStandalone,
  ],
  providers: [IgxExcelExporterService],
  templateUrl: './tenant-report.component.html',
  styleUrl: './tenant-report.component.scss',
})
export class TenantReportComponent implements OnInit {
  tenantData: ITenantReport[] = [];
  tenantDisplayData: ITenantReport[] = [];
  tenantExportData: ITenantReport[] = [];
  tenantRawData: ITenantReport[] = [];
  companyList: ICompaniesShortList[] = [];
  queryInProcess: boolean = false;
  queryCompleted: boolean = false;

  tenantSearchForm = new FormGroup({
    companyId: new FormControl('NULL', []),
    asOfDate: new FormControl(new Date(), []),
  });

  //Services
  public _appGeneralState = inject(AppGeneralStateService);
  _dataService = inject(DataService);
  _destroyRefService = inject(DestroyRef);
  _messageDialog = inject(MessageDialogService);
  _excelExportService = inject(IgxExcelExporterService);
  _datePipe = inject(DatePipe);

  constructor() {}

  ngOnInit(): void {
    this.getCompanyListForLotNumber(this._appGeneralState.currentLotNumber);

    this._appGeneralState.lotNumberSubject.subscribe({
      next: (v) => this.onLotNumberChange(v),
    });
  }

  onClearTenantSearchForm(): void {
    this.tenantSearchForm.setValue({
      asOfDate: new Date(),
      companyId: 'NULL',
    });

    this.tenantData = [];
    this.tenantRawData = [];
    this.tenantDisplayData = [];
    this.tenantExportData = [];

    this._messageDialog.snackBarTopCenterTimed('Tenant Roster Search Criteria and Results have been reset.', 4000);
  }

  onDownloadRawRosterReport(): void {
    var exportData: ITenantExportReport[] = [];
    const dateTitle: string = this._datePipe.transform(new Date(), 'shortDate');
    const exportFileName: string = `lot-${this._appGeneralState.currentLotNumber}-roster-${dateTitle}`;

    exportData = this.prepExportDataFile(this.tenantExportData);

    setTimeout(() => {
      var exporterOptions: IgxExcelExporterOptions = new IgxExcelExporterOptions(exportFileName);
      exporterOptions.worksheetName = `Lot ${this._appGeneralState.currentLotNumber} - ${this._appGeneralState.currentLotName}`;
      exporterOptions.columnWidth = 25;

      this._excelExportService.exportData(this.tenantDisplayData, exporterOptions);
      this._messageDialog.snackBarTopCenterTimed('The Roster has been downloaded.', 4000);
    }, 500);
  }

  onDownloadTenantReportPreparedFile(): void {
    var exportData: ITenantExportReport[] = [];
    const dateTitle: string = this._datePipe.transform(new Date(), 'MM-dd-yyyy');
    const exportFileName: string = `Lot ${this._appGeneralState.currentLotNumber} - Tenant Roster Report ${dateTitle}`;

    exportData = this.prepExportDataFile(this.tenantExportData);

    setTimeout(() => {
      var exporterOptions: IgxExcelExporterOptions = new IgxExcelExporterOptions(exportFileName);
      exporterOptions.worksheetName = `Lot ${this._appGeneralState.currentLotNumber} - ${this._appGeneralState.currentLotName}`;
      exporterOptions.columnWidth = 25;

      this._excelExportService.exportData(exportData, exporterOptions);
      this._messageDialog.snackBarTopCenterTimed('The Tenant Roster has been downloaded.', 4000);
    }, 500);
  }

  prepExportDataFile(rawFile: ITenantReport[]): ITenantExportReport[] {
    var exportData: ITenantExportReport[] = [];
    var ii: number = 0;

    for (ii = 0; ii < rawFile.length; ii++) {
      var currentRow: ITenantExportReport = {} as ITenantExportReport;

      currentRow.companyName = rawFile[ii].companyName;
      currentRow.parkerName = rawFile[ii].parkerName;
      currentRow.stallType = rawFile[ii].stallType;
      currentRow.vehicleLicense = rawFile[ii].vehicleLicense;
      currentRow.cardNumber = rawFile[ii].cardNumber;
      currentRow.stallNumber = rawFile[ii].stallNumber;
      currentRow.parkerPayType = rawFile[ii].parkerPayType;
      currentRow.companyPaid = rawFile[ii].companyPaid;
      currentRow.parkerPaid = rawFile[ii].parkerPaid;
      currentRow.total = rawFile[ii].total;

      exportData.push(currentRow);
    }

    return exportData;
  }

  onFetchTenantRoster(): void {
    var queryData: ITenantReportQueryCriteria = {} as ITenantReportQueryCriteria;

    queryData.asOfDate = new Date(this.tenantSearchForm.get('asOfDate').value);
    queryData.lotNumber = this._appGeneralState.currentLotNumber;
    queryData.companyId = this.tenantSearchForm.get('companyId').value;

    this.getTenantRosterData(queryData);
  }

  onLotNumberChange(lotNumber: any) {
    this.getCompanyListForLotNumber(lotNumber);
    this.onClearTenantSearchForm();
  }

  getCompanyListForLotNumber(lotNumber: string) {
    this._dataService.getCompanyListForLotNumber(lotNumber).subscribe(
      (companyList) => {
        this.companyList = companyList;
      },
      (err: any) => {},
      () => {},
    );
  }

  getTenantRosterData(queryData: ITenantReportQueryCriteria) {
    this.queryInProcess = true;
    this.queryCompleted = false;

    this._dataService
      .getTenantRosterData(queryData)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.tenantData = res;
          this.tenantDisplayData = JSON.parse(JSON.stringify(res));
          this.tenantRawData = JSON.parse(JSON.stringify(res));
          this.tenantExportData = JSON.parse(JSON.stringify(res));
        },
        error: (err: any) => {
          this.queryInProcess = false;
          this.queryCompleted = true;
          console.error('error getting roster', err);
        },
        complete: () => {
          this.queryInProcess = false;
          this.queryCompleted = true;
          this.tenantRosterPostProcess();
        },
      });
  }

  tenantRosterPostProcess(): void {
    // this.createTenantDisplayData();
    this.createTenantExportWithBreaks();
  }

  createTenantDisplayData(): void {
    let ii: number = 0;

    for (ii = 0; ii < this.tenantDisplayData.length; ii++) {
      this.tenantDisplayData[ii].total = this.tenantDisplayData[ii]?.rateAmount ?? 0;
      if ((this.tenantDisplayData[ii].parkerPayType = 'Parker')) {
        this.tenantDisplayData[ii].parkerPaid = this.tenantDisplayData[ii]?.rateAmount ?? 0;
        this.tenantDisplayData[ii].companyPaid = 0;
      } else {
        this.tenantDisplayData[ii].companyPaid = this.tenantDisplayData[ii]?.rateAmount ?? 0;
        this.tenantDisplayData[ii].parkerPaid = 0;
      }
    }
  }

  createTenantExportWithBreaks(): void {
    this.tenantExportData = [];
    var ii: number = 0;
    var currentCompany: string = this.tenantRawData[0].companyName;
    var currentCompanyId: string = this.tenantRawData[0].companyId;
    var parkerSubtotal: number = 0;
    var companySubtotal: number = 0;
    var grandSubtotal: number = 0;
    var parkerGrandTotal: number = 0;
    var companyGrandTotal: number = 0;
    var grandGrandTotal: number = 0;
    var totals: ITenantReportCompanyTotals = {} as ITenantReportCompanyTotals;

    for (ii = 0; ii < this.tenantRawData.length; ii++) {
      if (currentCompany != this.tenantRawData[ii].companyName) {
        //this is a new company

        const companyCount: number = this.getCurrentCompanyCount(currentCompanyId);
        totals = this.getCurrentCompanyTotals(currentCompanyId);

        var blankLine: ITenantReport = {} as ITenantReport;
        blankLine.companyName = '';
        blankLine.parkerPayType = `# of Parkers by Company: ${companyCount}`;
        blankLine.total = totals.total;
        blankLine.parkerPaid = totals.parkerPaid;
        blankLine.companyPaid = totals.companyPaid;
        this.tenantExportData.push(blankLine);

        blankLine = {} as ITenantReport;
        blankLine.parkerPayType = ' ';
        this.tenantExportData.push(blankLine);

        currentCompany = this.tenantRawData[ii].companyName;
        currentCompanyId = this.tenantRawData[ii]?.companyId;

        this.tenantExportData.push(this.tenantRawData[ii]);
      } else {
        this.tenantExportData.push(this.tenantRawData[ii]);
        // if (displayCompanyName) {
        //   displayCompanyName = false;
        // } else {
        //   this.tenantExportData[ii].companyName = '';
        // }
      }
    }

    //Update for displaying companies names on first row of company
    currentCompany = '';
    for (ii = 0; ii < this.tenantExportData.length; ii++) {
      if (currentCompany == this.tenantExportData[ii].companyName) {
        this.tenantExportData[ii].companyName = '';
      } else {
        currentCompany = this.tenantExportData[ii].companyName;

        this.tenantExportData[ii].companyName = currentCompany;
      }
    }

    // Get the subtotals for last company
    const companyCount: number = this.getCurrentCompanyCount(currentCompanyId);
    totals = this.getCurrentCompanyTotals(currentCompanyId);

    var blankLine: ITenantReport = {} as ITenantReport;
    blankLine.companyName = '';
    blankLine.parkerPayType = `# of Parkers by Company: ${companyCount}`;
    blankLine.total = totals.total;
    blankLine.parkerPaid = totals.parkerPaid;
    blankLine.companyPaid = totals.companyPaid;
    this.tenantExportData.push(blankLine);

    blankLine = {} as ITenantReport;
    blankLine.parkerPayType = ' ';
    this.tenantExportData.push(blankLine);

    // Add Grand Totals
    var grandTotals: ITenantReportCompanyTotals = {} as ITenantReportCompanyTotals;

    grandTotals = this.getGrandTotals();

    var blankLine: ITenantReport = {} as ITenantReport;
    blankLine.companyName = '';
    blankLine.parkerPayType = `# of Parkers: ${this.tenantDisplayData.length}`;
    blankLine.total = grandTotals.total;
    blankLine.parkerPaid = grandTotals.parkerPaid;
    blankLine.companyPaid = grandTotals.companyPaid;
    this.tenantExportData.push(blankLine);
  }

  getCurrentCompanyCount(companyId: string): number {
    let companyCount: number = 13;

    companyCount = this.tenantRawData.filter((company) => company.companyId == companyId).length;
    return companyCount;
  }

  getCurrentCompanyTotals(companyId: string): ITenantReportCompanyTotals {
    var totals: ITenantReportCompanyTotals = {} as ITenantReportCompanyTotals;
    var ii: number = 0;

    totals.parkerPaid = 0;
    totals.companyPaid = 0;
    totals.total = 0;

    for (ii = 0; ii < this.tenantRawData.length; ii++) {
      if (this.tenantRawData[ii].companyId == companyId) {
        totals.parkerPaid += this.tenantRawData[ii].parkerPaid;
        totals.companyPaid += this.tenantRawData[ii].companyPaid;
        totals.total += this.tenantRawData[ii].total;
      }
    }

    return totals;
  }

  getGrandTotals(): ITenantReportCompanyTotals {
    var totals: ITenantReportCompanyTotals = {} as ITenantReportCompanyTotals;
    var ii: number = 0;

    totals.parkerPaid = 0;
    totals.companyPaid = 0;
    totals.total = 0;

    for (ii = 0; ii < this.tenantDisplayData.length; ii++) {
      totals.parkerPaid += this.tenantDisplayData[ii].parkerPaid;
      totals.companyPaid += this.tenantDisplayData[ii].companyPaid;
      totals.total += this.tenantDisplayData[ii].total;
    }

    return totals;
  }
}
